import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import { useRouter } from "next/router";
import { SourceToCountry } from "@components/Features/catalog/catalog-utils";
import MapView from "@components/Features/catalog/map-view/map-view";
import MainFilter from "@components/Features/MainPage/MainFilter/MainFilter";
import { Aggregations } from "@src/interfaces/map-response.scheme";
import { AppState } from "@app/store/store";
import { generateCatalogHeader1 } from "@features/seo";
import { CatalogContent } from "./CatalogContent";
import { CatalogSEO } from "./components/CatalogSEO";
import { CatalogScreenProps } from "./constants";
import {
    useAnalyticsFilters,
    useCatalogData,
    useCatalogFilters,
    useMapMode,
    usePreviewQuantity,
    useSEOData
} from "./hooks";
import { useCatalogActions } from "./hooks/useCatalogActions";
import classes from "./CatalogScreen.module.scss";

const CatalogScreen = ({ preloadedProps }: CatalogScreenProps) => {
    const ssrOffers = preloadedProps?.preloadOffers || [];
    const ssrQuantity = preloadedProps?.preloadQuantity || 0;
    const ssrTotalPages = preloadedProps?.preloadTotalPages || 1;
    const ssrOgImage = preloadedProps?.preloadOffers?.[0]?.imagesList?.[0]?.big || null;

    const router = useRouter();

    const filtersHook = useCatalogFilters();
    const mapHook = useMapMode();
    const previewHook = usePreviewQuantity(filtersHook.updateFilters);

    const catalogHook = useCatalogData({
        filters: filtersHook.filters,
        mapListMode: mapHook.mapListMode,
        formattedCoordinates: mapHook.formattedCoordinates,
        newZoom: mapHook.newZoom,
        createdParamSort: filtersHook.createdParamSort,
        createdTimePrev: filtersHook.createdTimePrev,
        ssrOffers,
        ssrQuantity,
        ssrTotalPages
    });

    const { analyticsData } = useAnalyticsFilters({
        brandValue: filtersHook.brandValue,
        modelValue: filtersHook.modelValue,
        yearFromValue: filtersHook.yearFromValue,
        generationValue: filtersHook.generationValue,
        bodyValue: filtersHook.bodyValue,
        transmissionValue: filtersHook.transmissionValue,
        engineTypeValue: filtersHook.engineTypeValue,
        driveValue: filtersHook.driveValue,
        engineVolumeFromValue: filtersHook.engineVolumeFromValue
    });

    const seoHook = useSEOData({
        preloadedProps,
        brandValue: filtersHook.brandValue,
        modelValue: filtersHook.modelValue,
        generationValue: filtersHook.generationValue,
        citiesValue: filtersHook.citiesValue,
        country: filtersHook.country,
        ssrQuantity,
        currentPage: catalogHook.currentPage,
        totalPages: catalogHook.totalPages,
        canonicalPath: router.asPath.split("?")[0]
    });

    const actionsHook = useCatalogActions({
        setSkip: catalogHook.setSkip,
        setFilters: filtersHook.setFilters,
        brandValue: filtersHook.brandValue,
        modelValue: filtersHook.modelValue,
        generationValue: filtersHook.generationValue,
        yearFromValue: filtersHook.yearFromValue,
        bodyValue: filtersHook.bodyValue,
        transmissionValue: filtersHook.transmissionValue,
        engineTypeValue: filtersHook.engineTypeValue,
        driveValue: filtersHook.driveValue,
        priceFromValue: filtersHook.priceFromValue,
        priceToValue: filtersHook.priceToValue,
        citiesValue: filtersHook.citiesValue,
        sourceId: filtersHook.sourceId,
        country: filtersHook.country,
        mapMode: mapHook.mapMode
    });

    useEffect(() => {
        catalogHook.debounceSetQnt(previewHook.updatePreviewQntData?.meta?.total);
    }, [previewHook.updatePreviewQntData, catalogHook.debounceSetQnt]);

    useEffect(() => {
        const isPreviewDataFetching =
            previewHook.updatePreviewQntIsFetching ||
            previewHook.updatePreviewQntIsLoading ||
            catalogHook.isFetching ||
            catalogHook.isLoading;
        catalogHook.debounceSetIsPreviewDataLoading(isPreviewDataFetching);
    }, [
        previewHook.updatePreviewQntIsFetching,
        catalogHook.isFetching,
        catalogHook.isLoading,
        previewHook.updatePreviewQntIsLoading,
        catalogHook.debounceSetIsPreviewDataLoading
    ]);

    useEffect(() => {
        if (catalogHook.data && catalogHook.data.offers) {
            catalogHook.setDataMemory(catalogHook.data.offers);
            catalogHook.setTotalPages(catalogHook.data.meta.totalPage);
        }

        if (catalogHook.currentPage === 1) {
            const sourceIdSingle =
                filtersHook.sourceId && filtersHook.sourceId.length === 1
                    ? filtersHook.sourceId[0]
                    : undefined;
            const params = {
                brand: filtersHook.brandValue?.name,
                model: filtersHook.modelValue?.name,
                generation: filtersHook.generationValue?.name,
                qnt: catalogHook.data?.meta?.total || 0,
                city: filtersHook.citiesValue[0]?.name,
                country: sourceIdSingle ? SourceToCountry[sourceIdSingle] : undefined
            };
            const newH1 = generateCatalogHeader1(params);
            seoHook.setHeader1(newH1);
        }
    }, [
        catalogHook.data,
        catalogHook.currentPage,
        catalogHook.setDataMemory,
        catalogHook.setTotalPages,
        filtersHook.sourceId,
        filtersHook.brandValue,
        filtersHook.modelValue,
        filtersHook.generationValue,
        filtersHook.citiesValue,
        seoHook.setHeader1
    ]);

    return (
        <div className={classes["catalog-screen"]}>
            <CatalogSEO
                titleTagContent={seoHook.titleTagContent}
                description={seoHook.description}
                keywords={seoHook.keywords}
                canonicalWithPage={seoHook.canonicalWithPage}
                metaRobots={seoHook.metaRobots}
                ssrOgImage={ssrOgImage}
                seoSsrOgImage={seoHook.ssrOgImage}
                linkRelPrev={seoHook.linkRelPrev}
                linkRelNext={seoHook.linkRelNext}
                breadcrumbSchema={seoHook.breadcrumbSchema}
                productsCarSchema={seoHook.productsCarSchema}
            />
            <div className={classes["catalog-screen__inner"]}>
                <div className={classes["catalog-screen__border"]}>
                    <div className={classes["catalog-screen__filter"]}>
                        <MainFilter
                            previewQnt={catalogHook.previewQnt}
                            isPreviewQntFetching={catalogHook.isPrevDataLoading}
                            onFilterSubmit={async (forceFilters: Record<string, string>) => {
                                await actionsHook.applyFilters(false, forceFilters);
                                actionsHook.handleScrollToCatalog();
                            }}
                            resetFilters={actionsHook.reset}
                            isMainPage={false}
                            mapMode={mapHook.mapMode}
                            disableMapListMode={mapHook.handleDisableMapListMode}
                        />
                    </div>
                </div>

                {mapHook.mapMode && (
                    <MapView
                        filters={filtersHook.filters}
                        onBoundsChanged={mapHook.handleBoundsChange}
                        total={catalogHook.data?.meta?.total}
                        isDataLoading={catalogHook.isFetching || catalogHook.isLoading}
                        data={catalogHook.data?.aggregations?.geotile_grid as Aggregations[]}
                        setViewMode={mapHook.handleViewModeChange}
                    />
                )}

                {!mapHook.mapMode && (
                    <CatalogContent
                        error={catalogHook.error}
                        analyticsData={analyticsData}
                        catalogTimeSort={filtersHook.catalogTimeSort}
                        catalogAllParamsSort={filtersHook.catalogAllParamsSort}
                        dataMemory={catalogHook.dataMemory}
                        isFetching={catalogHook.isFetching}
                        isLoading={catalogHook.isLoading}
                        currentPage={catalogHook.currentPage}
                        header1={seoHook.header1}
                        data={catalogHook.data}
                        totalPages={catalogHook.totalPages}
                    />
                )}
            </div>
        </div>
    );
};
export default CatalogScreen;